import { faFill, faSearch, faSwatchbook } from '@fortawesome/pro-regular-svg-icons';
import { cva } from 'class-variance-authority';
import { useTranslation } from 'next-i18next';

import { colorCardProductSlugs, colorTestProductSlugs, paintguidePageSlugs } from '@boss/constants/b2b-b2c';
import { Color, Nullable, SectionFields } from '@boss/types/b2b-b2c';
import { Button, Carousel, ColorButton, InspirationImage, Presence, Section } from '@boss/ui';
import { getTranslatedSlugsByType } from '@boss/utils';

import { ColorGridWrapper, ColorToolSection, CtaCardsSection } from '../../components';
import { ComponentMapper } from '../../components/Mappers';
import { INSP_IMAGES_CAROUSEL_SETINGS } from '../../constants';
import { useFavorites, useRecentViewedColors } from '../../hooks';
import { isB2b } from '../../utils';

type Props = {
  color: Color;
  locale: string;
  colorToolSection?: Nullable<SectionFields>;
  ctaSection?: Nullable<SectionFields>;
};

const ButtonStyling = cva('min-h-12 w-full justify-center md:w-fit [&>span]:w-fit', {
  variants: {
    outlined: {
      true: 'border-brown-dark border-1',
      false: '',
    },
  },
});

const ColorDetailPage = ({ color, locale, colorToolSection, ctaSection }: Props) => {
  const { t } = useTranslation(['color', 'common']);
  const { addColor, viewedColors } = useRecentViewedColors();
  const { favorites, toggleFavorite } = useFavorites();

  const colorIdColorCard = `${color.id}_45`;

  return (
    <div>
      <Presence id="color-detail-page-top-section" visible>
        <Section
          className="pb-10 pt-0 md:pb-20"
          content={
            <div className="flex flex-col gap-16">
              <div className="grid gap-6 md:grid-cols-3 md:flex-row md:gap-10">
                <div className="flex flex-col gap-6 md:gap-10">
                  <h1 className="font-normal">
                    <div className="block first-letter:uppercase">{t('paintColor')}:</div>
                    <b className="md:block">{color.code}</b>
                    {` `}
                    <b className="md:block">{color.name}</b>
                  </h1>

                  <ColorButton
                    backgroundColor={color.rgb}
                    className="w-fit"
                    isFavorite={favorites.COLORS.includes(color.code)}
                    onFavorite={() => toggleFavorite('COLORS', color.code, color)}
                    variant="secondary"
                  />
                  <div className="flex flex-col gap-4">
                    {!isB2b && (
                      <>
                        {color.sampleTypes?.includes('SampleColorPaint') && (
                          <Button
                            className={ButtonStyling()}
                            href={`${colorTestProductSlugs[locale]}?colorId=${color.id}`}
                            icon={faFill}
                            iconPosition="left"
                            label={t('testJar') ?? undefined}
                            type="primary"
                          />
                        )}
                        {color.sampleTypes?.includes('SampleColorCard') && (
                          <Button
                            className={ButtonStyling({ outlined: true })}
                            href={`${colorCardProductSlugs[locale]}?colorId=${colorIdColorCard}`}
                            icon={faSwatchbook}
                            iconPosition="left"
                            label={t('colorCard') ?? undefined}
                            type="secondary"
                          />
                        )}
                        <Button
                          className={ButtonStyling({ outlined: true })}
                          href={`${paintguidePageSlugs[locale]}?color=${color.id}`}
                          icon={faSearch}
                          iconPosition="left"
                          label={t('chooseColorType') ?? undefined}
                          type="secondary"
                        />
                      </>
                    )}
                  </div>
                </div>

                {color.inspirations && (
                  <div className="md:col-span-2">
                    <Carousel id="inspiration-images-carousel" options={INSP_IMAGES_CAROUSEL_SETINGS}>
                      {color.inspirations.map(inspiration => (
                        <InspirationImage
                          asset={inspiration?.image?.image}
                          className="mb-0"
                          imageClassName="rounded-md"
                          key={inspiration.slug}
                          showPinterestButton={!isB2b}
                          slug={getTranslatedSlugsByType(inspiration.slug, 'Inspiration overview')[locale]}
                        />
                      ))}
                    </Carousel>
                  </div>
                )}
              </div>
            </div>
          }
        />
      </Presence>

      <Presence
        className="md:col-span-full"
        id="related-color-detail-page-colors-presence"
        visible={!!color?.relatedColors?.length}
      >
        <Section
          backgroundColor="bg-beige-light"
          content={
            <div className="flex flex-col gap-10">
              <h3>{t('relatedColorsTitle', { name: color?.name })}</h3>
              <ColorGridWrapper
                colors={color?.relatedColors ?? []}
                gridClassName="grid-cols-2 sm:grid-cols-3 md:grid-cols-6"
                id="selected-colors"
                onChangeColorClick={addColor}
                onClickAction="navigate"
                translations={t('colorPickerModal', { ns: 'product', returnObjects: true })}
                variant="secondary"
              />
            </div>
          }
        />
      </Presence>

      <Presence
        className="md:col-span-full"
        id="related-color-detail-page-colors-presence"
        visible={!!viewedColors.length}
      >
        <Section
          backgroundColor="bg-beige-light"
          content={
            <div className="flex flex-col gap-10">
              <h3>{t('recentlyViewedColors', { ns: 'common' })}</h3>
              <ColorGridWrapper
                colors={viewedColors}
                gridClassName="grid-cols-2 sm:grid-cols-3 md:grid-cols-6"
                id="selected-colors"
                onChangeColorClick={addColor}
                onClickAction="navigate"
                translations={t('colorPickerModal', { ns: 'product', returnObjects: true })}
                variant="secondary"
              />
            </div>
          }
        />
      </Presence>

      {!!ctaSection?.content?.length && <CtaCardsSection ctaSection={ctaSection} />}
      {colorToolSection && !isB2b && (
        <ColorToolSection entry={colorToolSection} locale={locale} selectedColor={color} />
      )}
      {color?.generalContent?.colorTool && (
        <ComponentMapper entry={color.generalContent.colorTool} locale={locale} theme="Colora: green" />
      )}
    </div>
  );
};

export default ColorDetailPage;
